// Tables are the first feature that is not part of every editor. `@lexical/table` and the code
// around it are about a fifth of what an editor costs, and a composer never needs them, so they
// sit behind a dynamic import and are fetched when a document holds a table or an editor offers
// to make one. A Lexical editor cannot be given node types after it is built, so the load always
// finishes before the editor exists; nothing is ever rebuilt to add tables.
// Type only, so it is erased: the one real import of this module is the dynamic one below.
import type * as Tables from "../features/tables/index";
import {
  createModuleLoader,
  type ModuleLoader,
  type ModuleStatus,
  type ModuleSupport,
} from "./module-loader";

export type TablesModule = typeof Tables;

const loader = createModuleLoader<TablesModule>(() => import("../features/tables/index"));

/** Starts the load if nothing has, and again after a failure. Safe to call on every render. */
export const loadTables = (): void => loader.load();

/** Where the load of table support stands, for every editor on the page: it is loaded once. */
export type TableSupportPhase = ModuleStatus<TablesModule>["phase"];

/**
 * Read-only, and shaped for `useSyncExternalStore`. For a host that wants to say so while table
 * support arrives, or to decide whether `preloadTables()` is worth calling. Asking never starts
 * the load.
 */
export const tableSupport: ModuleSupport<TablesModule> = {
  subscribe: loader.subscribe,
  getPhase: () => loader.get().phase,
};

export const tablesStatus: Pick<ModuleLoader<TablesModule>, "subscribe" | "get"> = {
  subscribe: loader.subscribe,
  get: loader.get,
};

/**
 * Fetches table support ahead of time, for a host that knows a table is coming (a link to a
 * document that has one, a hover over "New table"). Resolves when editors can use it.
 */
export const preloadTables = (): Promise<void> => loader.preload();
